import { ReactElement, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '../layouts/PublicLayout';
import { Accordion } from '@/components/Accordion';

const Faq = (): ReactElement => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('faq');

    type FaqEntry = {
        title: string;
        content: string;
    };

    const generalItems = useMemo(() => {
        return t('sections.general.items', { returnObjects: true }) as FaqEntry[];
    }, [t]);

    const subscriptionItems = useMemo(() => {
        return t('sections.subscription.items', { returnObjects: true }) as FaqEntry[];
    }, [t]);

    const metaTitle = t('meta.title');
    const metaDescription = t('meta.description');
    const pageTitle = t('title');
    const generalHeading = t('sections.general.heading');
    const subscriptionHeading = t('sections.subscription.heading');
    const buttonLabel = t('button');

    const currentLang = (i18n.language || 'ja').split('-')[0];
    const htmlLang = currentLang === 'ja' ? 'ja' : 'en';
    const brandName = htmlLang === 'en' ? 'Kotomil' : 'コトミル';
    const baseUrl = 'https://kotomil.com/faq';
    const canonicalUrl = currentLang === 'en' ? `${baseUrl}?lang=en` : baseUrl;
    const ogLocale = htmlLang === 'ja' ? 'ja_JP' : 'en_US';
    const ogAlternateLocale = htmlLang === 'ja' ? 'en_US' : 'ja_JP';
    const alternateLinks = [
        { lang: 'ja', href: baseUrl },
        { lang: 'en', href: `${baseUrl}?lang=en` },
        { lang: 'x-default', href: baseUrl },
    ];

    return (
        <PublicLayout>
            <Helmet htmlAttributes={{ lang: htmlLang }}>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href={canonicalUrl} />
                {alternateLinks.map(({ lang, href }) => (
                    <link key={lang} rel="alternate" hrefLang={lang} href={href} />
                ))}

                <meta property="og:type" content="website" />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:site_name" content={brandName} />
                <meta
                    property="og:image"
                    content="https://kotomil.com/images/ogp/kotomil_og_image.jpg"
                />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:locale" content={ogLocale} />
                <meta property="og:locale:alternate" content={ogAlternateLocale} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@KotomilApp" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta
                    name="twitter:image"
                    content="https://kotomil.com/images/ogp/kotomil_og_image.jpg"
                />
            </Helmet>

            <div className="w-full max-w-5xl mx-auto px-4">
                {/* タイトル */}
                <h1 className="text-center text-3xl font-bold pt-10 m-0">{pageTitle}</h1>

                {/* FAQ（一般） */}
                <h2 className="text-xl font-bold py-10 mt-2">{generalHeading}</h2>
                <div className="space-y-5">
                    <Accordion
                        items={generalItems}
                        className="rounded-lg border-2 border-gray-300"
                        titleClassName="font-medium text-lg lg:text-xl p-4 border-b border-gray-300"
                        contentClassName="bg-gray-50 leading-loose tracking-widest p-5 text-md lg:text-base"
                    />
                </div>

                {/* FAQ（サブスクリプション） */}
                <h2 className="text-xl font-bold py-10 m-0">{subscriptionHeading}</h2>
                <div className="space-y-5">
                    <Accordion
                        items={subscriptionItems}
                        className="rounded-lg border-2 border-gray-300"
                        titleClassName="font-medium text-lg lg:text-xl p-4 border-b border-gray-300"
                        contentClassName="bg-gray-50 leading-loose tracking-widest p-5 text-md lg:text-base"
                    />
                </div>

                {/* ボタン */}
                <div className="mt-20 mb-10 text-center">
                    <button
                        type="button"
                        onClick={() => navigate('/desk')}
                        className="inline-block px-6 py-3 border border-gray-300 rounded hover:bg-gray-100 transition-colors w-full max-w-xs"
                    >
                        {buttonLabel}
                    </button>
                </div>
            </div>
        </PublicLayout>
    );
};

export default Faq;
